// Unified Admin data context — loads once on mount, refreshes only on demand.
import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { 
  fetchDashboardBundle, 
  RealtimeStats, 
  DashboardBundle,
  fetchFeatureFlags,
  FeatureFlag,
  fetchApiKeys,
  ApiKey,
  fetchSiteSettings,
  fetchActivity,
  AddressActivity,
  fetchAbuseReports,
  AbuseReport,
} from '../lib/api';

interface AdminData {
  dashboard: DashboardBundle | null;
  realtime: { activeNow: number; eventsLastHour: number; emailsToday: number; totalVisits: number; liveBreakdown: any[]; messages24h: number };
  featureFlags: FeatureFlag[];
  apiKeys: any[];
  siteSettings: Record<string, string>;
  activity: any[];
  abuseReports: any[];
  lastUpdated: number;
  error: string | null;
}

interface AdminPollingContextValue {
  data: AdminData;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AdminPollingContext = createContext<AdminPollingContextValue | null>(null);

export function AdminPollingProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AdminData>({
    dashboard: null,
    realtime: { activeNow: 0, eventsLastHour: 0, emailsToday: 0, totalVisits: 0, liveBreakdown: [], messages24h: 0 },
    featureFlags: [],
    apiKeys: [],
    siteSettings: {},
    activity: [],
    abuseReports: [],
    lastUpdated: 0,
    error: null,
  });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    // Never poll without a valid admin token — avoids 401 storms / reload loops
    if (!localStorage.getItem('admin_token')) return;
    try {
      // Parallel fetch all admin data (realtime comes from dashboard bundle — skip extra stats fetch)
      const [
        dashboard,
        featureFlags,
        apiKeys,
        siteSettings,
        activity,
        abuseReports,
      ] = await Promise.allSettled([
        import('../lib/api').then(m => m.fetchDashboardBundle('5min')),
        import('../lib/api').then(m => m.fetchFeatureFlags()),
        import('../lib/api').then(m => m.fetchApiKeys()),
        import('../lib/api').then(m => m.fetchSiteSettings()),
        import('../lib/api').then(m => m.fetchActivity(20)),
        import('../lib/api').then(m => m.fetchAbuseReports()),
      ]);

      if (!mountedRef.current) return;

      setData(prev => ({
        dashboard: dashboard.status === 'fulfilled' ? dashboard.value : prev.dashboard,
        realtime:
          dashboard.status === 'fulfilled' && dashboard.value?.realtime
            ? dashboard.value.realtime
            : prev.realtime,
        featureFlags: featureFlags.status === 'fulfilled' ? featureFlags.value : prev.featureFlags,
        apiKeys: apiKeys.status === 'fulfilled' ? apiKeys.value : prev.apiKeys,
        siteSettings: siteSettings.status === 'fulfilled' ? siteSettings.value : prev.siteSettings,
        activity: activity.status === 'fulfilled' ? activity.value : prev.activity,
        abuseReports: abuseReports.status === 'fulfilled' ? abuseReports.value : prev.abuseReports,
        lastUpdated: Date.now(),
        error: null,
      }));
    } catch (err) {
      console.error('Admin poll error:', err);
      setData(prev => ({ ...prev, error: String(err) }));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchAll();
  }, []);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (!localStorage.getItem('admin_token')) {
      setLoading(false);
      return;
    }
    const init = async () => {
      // Sequential initial load for critical data first
      await import('../lib/api').then(m => m.fetchDashboardBundle('5min')).then(b => {
        if (!mountedRef.current) return;
        setData(prev => ({ ...prev, dashboard: b, realtime: b?.realtime ?? prev.realtime, loading: false }));
      }).catch(() => {});

      // Load rest in parallel
      await Promise.allSettled([
        import('../lib/api').then(m => m.fetchFeatureFlags()),
        import('../lib/api').then(m => m.fetchApiKeys()),
        import('../lib/api').then(m => m.fetchSiteSettings()),
        import('../lib/api').then(m => m.fetchActivity(20)),
        import('../lib/api').then(m => m.fetchAbuseReports()),
      ]).then(([flags, keys, settings, activity, abuse]) => {
        if (!mountedRef.current) return;
        setData(prev => ({
          ...prev,
          featureFlags: flags.status === 'fulfilled' ? flags.value : [],
          apiKeys: keys.status === 'fulfilled' ? keys.value : [],
          siteSettings: settings.status === 'fulfilled' ? settings.value : {},
          activity: activity.status === 'fulfilled' ? activity.value : [],
          abuseReports: abuse.status === 'fulfilled' ? abuse.value : [],
          loading: false,
          lastUpdated: Date.now(),
        }));
      });
    };
    init();
  }, []);

  // Reload-based only: the admin panel fetches once on mount and again when
  // the user explicitly reloads / hits refresh. No background polling --
  // each dashboard-bundle call costs ~7k D1 rows read.

  // Cleanup on unmount
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  return (
    <AdminPollingContext.Provider value={{ data, loading, refresh: () => fetchAll() }}>
      {children}
    </AdminPollingContext.Provider>
  );
}

export function useAdminData() {
  const ctx = useContext(AdminPollingContext);
  if (!ctx) throw new Error('useAdminData must be used within AdminPollingProvider');
  return ctx;
}

// Helper hooks for specific data slices
export function useDashboard() {
  const { data, loading } = useAdminData();
  return { dashboard: data.dashboard, loading };
}

export function useRealtime() {
  const { data } = useAdminData();
  return data.realtime;
}

export function useFeatureFlags() {
  const { data, refresh } = useAdminData();
  return { flags: data.featureFlags, refresh };
}

export function useApiKeys() {
  const { data, refresh } = useAdminData();
  return { keys: data.apiKeys, refresh };
}

export function useSiteSettings() {
  const { data, refresh } = useAdminData();
  return { settings: data.siteSettings, refresh };
}

export function useActivity() {
  const { data } = useAdminData();
  return data.activity;
}

export function useAbuseReports() {
  const { data } = useAdminData();
  return data.abuseReports;
}

export function useAdminRefresh() {
  const { refresh } = useAdminData();
  return refresh;
}

export function useLastUpdated() {
  const { data } = useAdminData();
  return data.lastUpdated;
}